<script setup>
import { reactive, ref, shallowRef, watch } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { required } from '@/utils/validation';

// Edit a user's name and, for faculty, what their Form 29 prints: birthdate,
// area of specialization (`department`) and assignments.
const props = defineProps({
  account: { type: Object, default: null },
  faculty: { type: Boolean, default: true },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const toast = useToast();
const formRef = ref(null);
const saving = shallowRef(false);
const form = reactive({ fname: '', mname: '', lname: '', bdate: '', department: '', teaching_related_assignments: '', special_assignments: '' });
const today = new Date().toISOString().slice(0, 10);

watch(open, (isOpen) => {
  if (!isOpen || !props.account) return;
  form.fname = props.account.fname ?? '';
  form.mname = props.account.mname ?? '';
  form.lname = props.account.lname ?? '';
  form.bdate = props.account.bdate ?? '';
  form.department = props.account.department ?? '';
  form.teaching_related_assignments = props.account.teaching_related_assignments ?? '';
  form.special_assignments = props.account.special_assignments ?? '';
  formRef.value?.resetValidation();
});

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  saving.value = true;
  try {
    const data = {
      fname: form.fname.trim(),
      mname: form.mname.trim(),
      lname: form.lname.trim(),
    };
    // The endpoint reads the values from the URL segment.
    const body = await apiRequest(
      'post',
      `/user-details/updateUserInfo/${props.account.id}/${encodeURIComponent(
        JSON.stringify(data),
      )}`,
      data,
    );
    assertOk(body);
    if (props.faculty) {
      await apiRequest('patch', `/user-details/${props.account.id}/form-details`, {
        bdate: form.bdate,
        department: form.department.trim(),
        teaching_related_assignments: form.teaching_related_assignments.trim(),
        special_assignments: form.special_assignments.trim(),
      });
    }
    toast.success(body.msg || 'Saved.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the name.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Personal information"
    :description="account?.name"
    size="md"
  >
    <v-form ref="formRef" class="form-grid" @submit.prevent="save">
      <UiFormField label="First name" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.fname"
          :rules="[required]"
          hide-details="auto"
          autocomplete="given-name"
        />
      </UiFormField>
      <UiFormField label="Middle name" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.mname"
          hide-details="auto"
          autocomplete="additional-name"
        />
      </UiFormField>
      <UiFormField label="Last name" class="span-2" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.lname"
          :rules="[required]"
          hide-details="auto"
          autocomplete="family-name"
        />
      </UiFormField>
      <template v-if="faculty">
        <p class="info__section span-2">Form 29 details</p>
        <UiFormField label="Birthdate" v-slot="{ id }">
          <v-text-field :id="id" v-model="form.bdate" type="date" :max="today" hide-details="auto" />
        </UiFormField>
        <UiFormField label="Area of Specialization" v-slot="{ id }">
          <v-text-field :id="id" v-model="form.department" maxlength="150" hide-details="auto" />
        </UiFormField>
        <UiFormField label="Teaching Related Assignments" class="span-2" v-slot="{ id }">
          <v-textarea :id="id" v-model="form.teaching_related_assignments" rows="2" auto-grow maxlength="500" hide-details="auto" />
        </UiFormField>
        <UiFormField label="Ancillary Assignments" class="span-2" v-slot="{ id }">
          <v-textarea :id="id" v-model="form.special_assignments" rows="2" auto-grow maxlength="500" hide-details="auto" />
        </UiFormField>
      </template>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        Save changes
      </v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.info__section {
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 600;
}
</style>
